import { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  FileCode,
  X,
  CheckCircle2,
  AlertCircle,
  File,
} from 'lucide-react';
import {
  uploadModelSchema,
  type UploadModelFormData,
  BACKEND_TYPES,
  CONFIG_TEMPLATES,
  type BackendType,
} from './schemas';
import { useTritonModelStore, type UploadedModel } from '@/store/tritonModelStore';
import { cn } from '@/lib/utils';

interface UploadModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: string;
  onSuccess?: () => void;
}

interface UploadedFile {
  file: File;
  name: string;
}

const UploadModelDialog = ({
  open,
  onOpenChange,
  instanceId,
  onSuccess,
}: UploadModelDialogProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const addModel = useTritonModelStore((state) => state.addModel);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UploadModelFormData>({
    resolver: zodResolver(uploadModelSchema),
    defaultValues: {
      modelName: '',
      backend: 'onnxruntime',
      version: '1',
      maxBatchSize: 8,
      configPbtxt: CONFIG_TEMPLATES.onnxruntime,
    },
  });

  const selectedBackend = watch('backend') as BackendType;
  const modelName = watch('modelName');
  const maxBatchSize = watch('maxBatchSize');

  // Update config template when backend changes
  useEffect(() => {
    if (selectedBackend) {
      let config = CONFIG_TEMPLATES[selectedBackend];
      if (modelName) {
        config = config.replace('MODEL_NAME', modelName);
      }
      config = config.replace('MAX_BATCH_SIZE', String(maxBatchSize || 8));
      setValue('configPbtxt', config);
    }
  }, [selectedBackend, setValue]);

  // Update config when model name or batch size changes
  useEffect(() => {
    if (selectedBackend) {
      let config = CONFIG_TEMPLATES[selectedBackend];
      config = config.replace('MODEL_NAME', modelName || 'MODEL_NAME');
      config = config.replace('MAX_BATCH_SIZE', String(maxBatchSize || 8));
      setValue('configPbtxt', config);
    }
  }, [modelName, maxBatchSize, selectedBackend, setValue]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const newFiles = files.map((file) => ({
      file,
      name: file.name,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      name: file.name,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  }, []);

  const removeFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const simulateUpload = async () => {
    setUploadProgress(0);
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      setUploadProgress((i / steps) * 100);
    }
  };

  const onSubmit = async (data: UploadModelFormData) => {
    if (uploadedFiles.length === 0) {
      return;
    }

    setIsUploading(true);

    // Simulate upload progress
    await simulateUpload();

    // Create model entry
    const newModel: UploadedModel = {
      name: data.modelName,
      version: data.version,
      backend: data.backend as UploadedModel['backend'],
      maxBatchSize: data.maxBatchSize,
      configPbtxt: data.configPbtxt,
      files: uploadedFiles.map((f) => f.name),
      uploadedAt: Date.now(),
    };

    // Add to store
    addModel(instanceId, newModel);

    setIsUploading(false);
    setUploadProgress(0);
    setUploadedFiles([]);
    reset();
    onOpenChange(false);
    onSuccess?.();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setUploadedFiles([]);
      setUploadProgress(0);
    }
    onOpenChange(newOpen);
  };

  const getBackendFileExtensions = (backend: BackendType): string => {
    switch (backend) {
      case 'onnxruntime':
        return '.onnx';
      case 'tensorrt':
        return '.plan,.engine';
      case 'pytorch':
        return '.pt,.pth';
      case 'python':
        return '.py';
      case 'openvino':
        return '.xml,.bin';
      default:
        return '*';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload New Model
          </DialogTitle>
          <DialogDescription>
            Upload a new model to the Triton Inference Server repository.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Model Name and Backend */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="modelName">Model Name</Label>
              <Input
                id="modelName"
                placeholder="my-model"
                {...register('modelName')}
                className={errors.modelName ? 'border-red-500' : ''}
              />
              {errors.modelName && (
                <p className="text-xs text-red-500">{errors.modelName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="backend">Backend</Label>
              <Controller
                name="backend"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.backend ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select backend" />
                    </SelectTrigger>
                    <SelectContent>
                      {BACKEND_TYPES.map((backend) => (
                        <SelectItem key={backend.id} value={backend.id}>
                          {backend.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.backend && (
                <p className="text-xs text-red-500">{errors.backend.message}</p>
              )}
            </div>
          </div>

          {/* Version and Max Batch Size */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                placeholder="1"
                {...register('version')}
                className={errors.version ? 'border-red-500' : ''}
              />
              {errors.version && (
                <p className="text-xs text-red-500">{errors.version.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxBatchSize">Max Batch Size</Label>
              <Input
                id="maxBatchSize"
                type="number"
                placeholder="8"
                {...register('maxBatchSize')}
                className={errors.maxBatchSize ? 'border-red-500' : ''}
              />
              {errors.maxBatchSize && (
                <p className="text-xs text-red-500">{errors.maxBatchSize.message}</p>
              )}
            </div>
          </div>

          {/* Config.pbtxt Editor */}
          <div className="space-y-2">
            <Label htmlFor="configPbtxt" className="flex items-center gap-2">
              <FileCode className="h-4 w-4" />
              config.pbtxt
            </Label>
            <Textarea
              id="configPbtxt"
              {...register('configPbtxt')}
              className={cn(
                'font-mono text-xs h-48 resize-none bg-[#1a1b26] text-gray-300',
                errors.configPbtxt ? 'border-red-500' : ''
              )}
            />
            {errors.configPbtxt && (
              <p className="text-xs text-red-500">{errors.configPbtxt.message}</p>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Model Files</Label>
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                uploadedFiles.length === 0 && 'cursor-pointer'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                if (uploadedFiles.length === 0) {
                  document.getElementById('file-upload')?.click();
                }
              }}
            >
              <input
                id="file-upload"
                type="file"
                multiple
                accept={getBackendFileExtensions(selectedBackend)}
                className="hidden"
                onChange={handleFileSelect}
              />

              {uploadedFiles.length === 0 ? (
                <>
                  <Upload className="mx-auto h-10 w-10 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Drag and drop model files here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expected formats: {getBackendFileExtensions(selectedBackend)}
                  </p>
                </>
              ) : (
                <div className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-muted/30 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('file-upload')?.click();
                    }}
                  >
                    Add more files
                  </Button>
                </div>
              )}
            </div>
            {uploadedFiles.length === 0 && (
              <p className="text-xs text-amber-500">At least one model file is required</p>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Uploading model...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUploading || uploadedFiles.length === 0}
            >
              {isUploading ? (
                <>
                  <Upload className="mr-2 h-4 w-4 animate-pulse" />
                  Uploading...
                </>
              ) : uploadProgress === 100 ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Uploaded
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Model
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModelDialog;
